import { Link } from 'react-router-dom';
import { Property } from '@/types/property';
import { formatPrice } from '@/lib/utils';
import { Badge } from './ui/badge';
import { Bed, Bath, Square, Building, Building2, Home, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from "./ui/button";
import { useState, useMemo, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { GridFSImage } from './GridFSImage';
import { FavoriteButton } from './FavoriteButton';
import { useComparison } from '@/hooks/useComparison';
import { cn } from '@/lib/utils';
import { ImageIcon, Scale } from 'lucide-react';

export interface PropertyCardProps extends Property {
  isProject?: boolean;
  availableUnits?: number;
  priceRange?: { min: number; max: number };
  propertyRange?: { min: number; max: number };
  bedRange?: { min: number; max: number };
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isFeatured?: boolean;
}

// Función para obtener el color del badge según el tipo de operación
const getOperationTypeColor = (type: string) => {
  switch (type) {
    case 'sale':
      return 'bg-blue-500';
    case 'rent':
      return 'bg-green-500';
    case 'temporal':
      return 'bg-purple-500';
    default:
      return 'bg-gray-500';
  }
};

// Función para obtener el texto del tipo de operación
const getOperationTypeText = (type: string) => {
  switch (type) {
    case 'sale':
      return 'Venta';
    case 'rent':
      return 'Alquiler';
    case 'temporal':
      return 'Alquiler Temporal';
    default:
      return type;
  }
};

// Función para obtener el texto del tipo de propiedad
const getPropertyTypeText = (propertyType: string | undefined) => {
  if (!propertyType) return '';
  
  switch (propertyType.toLowerCase()) {
    case 'casa':
      return 'Casa';
    case 'apartamento':
      return 'Apartamento';
    case 'terreno':
      return 'Terreno';
    case 'local':
      return 'Local';
    case 'oficina':
      return 'Oficina';
    case 'casa_comercial':
      return 'Casa Comercial';
    case 'industrial':
      return 'Industrial';
    case 'parcelacion':
      return 'Parcelación';
    default:
      return propertyType.charAt(0).toUpperCase() + propertyType.slice(1);
  }
};

export function PropertyCard({
  _id,
  id,
  title,
  description,
  price,
  location,
  images,
  type,
  propertyType,
  bedrooms,
  bathrooms,
  area,
  isFeatured,
  onMouseEnter,
  onMouseLeave
}: PropertyCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { compareIds, addToCompare, removeFromCompare } = useComparison();
  const propertyId = (_id || id || '').toString();
  
  const isComparing = compareIds.includes(propertyId);
  const validImages = useMemo(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[PropertyCard] ID: ${propertyId} - Procesando imágenes:`, images);
    }

    if (!images) return [];
    
    // Asegurarse de que images sea un array
    const imagesArray = Array.isArray(images) ? images : [images];
    
    // Filtrar imágenes válidas
    const filtered = imagesArray.filter(img => 
      img && 
      typeof img === 'string' && 
      img !== 'undefined' && 
      img !== 'null' &&
      img.trim() !== ''
    );
    
    // Ordenar imágenes para priorizar las de /api/images/
    const sorted = [...filtered].sort((a, b) => {
      const aIsApiImage = a.includes('/api/images/');
      const bIsApiImage = b.includes('/api/images/');
      
      if (aIsApiImage && !bIsApiImage) return -1;
      if (!aIsApiImage && bIsApiImage) return 1;
      return 0;
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[PropertyCard] ID: ${propertyId} - Imágenes válidas:`, sorted);
    }
    
    return sorted;
  }, [images, propertyId]);

  // Asegurarse de que el índice de imagen actual sea válido
  useEffect(() => {
    if (validImages.length > 0 && currentImageIndex >= validImages.length) {
      setCurrentImageIndex(0);
    }
  }, [validImages, currentImageIndex]);

  // Determinar la imagen principal
  const mainImage = useMemo(() => {
    if (validImages.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[PropertyCard] ID: ${propertyId} - No hay imágenes válidas`);
      }
      return '';
    }
    
    // Asegurarse de que el índice sea válido
    const safeIndex = Math.max(0, Math.min(currentImageIndex, validImages.length - 1));
    
    // Usar la imagen en el índice actual
    const currentImage = validImages[safeIndex];
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[PropertyCard] ID: ${propertyId} - Imagen principal: ${currentImage} (índice: ${safeIndex})`);
    }
    
    return currentImage;
  }, [validImages, currentImageIndex, propertyId]);

  const operationTypeColor = getOperationTypeColor(type);
  const operationTypeText = getOperationTypeText(type);
  const propertyTypeText = getPropertyTypeText(propertyType);

  const handleImageNavigation = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (validImages.length <= 1) return;
    
    setCurrentImageIndex(prev => {
      const newIndex = prev > 0 ? prev - 1 : validImages.length - 1;
      if (process.env.NODE_ENV === 'development') {
        console.log(`[PropertyCard] Navegando a imagen previa: ${newIndex}`);
      }
      return newIndex;
    });
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (validImages.length <= 1) return;
    
    setCurrentImageIndex(prev => {
      const newIndex = prev < validImages.length - 1 ? prev + 1 : 0;
      if (process.env.NODE_ENV === 'development') {
        console.log(`[PropertyCard] Navegando a imagen siguiente: ${newIndex}`);
      }
      return newIndex;
    });
  };

  return (
    <div 
      className={cn(
        "group relative bg-parchment brutalist-border-sm brutalist-card-hover rounded-none overflow-hidden",
        isFeatured && "ring-2 ring-terracotta"
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Link 
        to={`/property/${propertyId}`}
        className="block"
      >
        <div className="relative aspect-[4/5] overflow-hidden group">
          <GridFSImage
            src={mainImage || ''}
            alt={title}
            size="full"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0"
            isFeatured={isFeatured}
            propertyType={propertyType}
          />
          
          {/* Brutalist Labels */}
          <div className="absolute top-4 left-4 flex flex-col gap-2">
            <span className={cn(
              "px-3 py-1 text-[10px] font-black uppercase tracking-widest text-parchment border border-ink",
              operationTypeColor === 'bg-blue-500' ? 'bg-ink' : 
              operationTypeColor === 'bg-green-500' ? 'bg-moss' : 
              operationTypeColor === 'bg-purple-500' ? 'bg-terracotta' : 'bg-ink'
            )}>
              {operationTypeText}
            </span>
          </div>

          <div className="absolute top-4 right-4 flex flex-col gap-2">
            <FavoriteButton propertyId={propertyId} />
            <Button
              size="icon"
              variant="secondary"
              className={cn(
                "h-10 w-10 rounded-none border border-ink shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all",
                isComparing ? "bg-terracotta text-parchment" : "bg-parchment text-ink hover:bg-ink hover:text-parchment"
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                isComparing ? removeFromCompare(propertyId) : addToCompare(propertyId);
              }}
              title={isComparing ? "Quitar de comparación" : "Agregar a comparación"}
            >
              <Scale className="h-5 w-5" />
            </Button>
          </div>

          <div className="absolute bottom-4 left-4 right-4 bg-parchment/90 backdrop-blur-sm p-4 border border-ink translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
             <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-tighter text-ink mb-2">
                <span className="flex items-center gap-1"><Bed className="h-3 w-3" /> {bedrooms} HAB</span>
                <span className="flex items-center gap-1"><Bath className="h-3 w-3" /> {bathrooms} BAÑOS</span>
                <span className="flex items-center gap-1"><Square className="h-3 w-3" /> {area} M²</span>
             </div>
             <button className="w-full bg-ink text-parchment py-2 text-xs font-black uppercase tracking-widest hover:bg-terracotta transition-colors">
               Explorar
             </button>
          </div>
          
          {/* Vertical Property Type Label */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 vertical-text bg-ink text-parchment px-1 py-4 text-[8px] font-black uppercase tracking-[0.3em] border-l border-ink">
            {propertyTypeText}
          </div>
        </div>
        
        <div className="p-4 bg-parchment">
          <h3 className="text-xl font-display font-black text-ink uppercase leading-none mb-2 group-hover:text-terracotta transition-colors line-clamp-1 italic">
            {title}
          </h3>
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-serif italic text-ink/60 line-clamp-1">
              {location?.address || 'Dirección no disponible'}
            </span>
            <span className="text-lg font-display font-black text-ink ml-4 whitespace-nowrap">
              ${formatPrice(price || 0)}
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}